import './scripts/lazyloading';
import Vue from 'vue';
import VueScreen from 'vue-screen';
import { formatMoney } from '@shopify/theme-currency';
import * as images from '@shopify/theme-images';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import { print } from 'graphql';
import ProductItem from './vue/components/ProductItem.vue';
import UpsellItem from './vue/components/UpsellItem.vue';
import Inventory from './vue/components/Inventory.vue';
import { emitter } from './utils/mitt';

// eslint-disable-next-line camelcase
import { px_breakpoints } from './utils/breakpoints';
import VPic from './vue/components/VPic.vue';
import Modal from './vue/components/Modal.vue';
// eslint-disable-next-line import/no-extraneous-dependencies
import '@splidejs/splide/dist/css/themes/splide-default.min.css';
import bem from './mixins/bem';

import { fetchWishlist } from './graphql/fetchWishlist.graphql';

const axios = require('axios').default;
const ls = require('local-storage');

/* config */
Vue.config.devtools = true;
Vue.config.productionTip = false;
Vue.config.silent = false;

/* extensions */
Vue.prototype.$language = window.PSTheme.Language;
Vue.prototype.$image = (src, size = '750x') =>
  images.getSizedImageUrl(src, size);
Vue.prototype.$preloadImages = (imgArray, size = '750x') =>
  images.preload(imgArray, size);
Vue.prototype.$money = (amount) => formatMoney(amount, '${{amount}}');
Vue.prototype.$isEmpty = (obj) => {
  if (obj && Object.keys(obj).length === 0 && obj.constructor === Object)
    return true;
};

/* plugins */
Vue.use(VueScreen, {
  mobile_up: px_breakpoints['--mobile-up'],
  tablet_up: px_breakpoints['--tablet-up'],
  tablet_lg_up: px_breakpoints['--tablet-lg-up'],
  laptop_up: px_breakpoints['--laptop-up'],
  desktop_up: px_breakpoints['--desktop-up'],
  medium_up: '768px',
});

/* mixins */
Vue.mixin(bem);

// Banner Carousels
document.querySelectorAll('.carousel').forEach((bannerCarousel) => {
  const ID = bannerCarousel.parentNode.id;
  window.PSTheme.Vue[`bannerCarousel${ID}`] = new Vue({
    name: 'bannerCarousel',
    components: {
      vpic: VPic,
      slider: Splide,
      slide: SplideSlide,
    },
    data() {
      return {
        mounted: false,
        carouselOptions: {
          type: 'loop',
          arrows: true,
          autoplay: true,
          interval: 3000,
          pagination: false,
          drag: 'free',

          breakpoints: {
            [px_breakpoints['--tablet-up']]: {
              arrows: false,
              pagination: true,
            },
          },
        },
      };
    },
    mounted() {
      this.mounted = true;
    },
  }).$mount(bannerCarousel);
});
// Incentive Carousel
document
  .querySelectorAll('.section-incentivebar')
  .forEach((incentiveCarousel) => {
    const ID = incentiveCarousel.parentNode.id;
    window.PSTheme.Vue[`incentiveCarousel${ID}`] = new Vue({
      name: 'incentiveCarousel',
      components: {
        slider: Splide,
        slide: SplideSlide,
      },
      data() {
        return {
          mounted: false,
          incentiveOptions: {
            type: 'loop',
            arrows: false,
            autoplay: true,
            interval: 2000,
            pagination: false,
            drag: 'free',
            breakpoints: {
              5000: {
                destroy: true,
              },
              639: {
                autoplay: true,
              },
            },
          },
        };
      },
      mounted() {
        this.mounted = true;
        this.$nextTick(() => {
          this.attachKlaviyoEvent();
        });
      },
      methods: {
        attachKlaviyoEvent() {
          const klaviyoTriggers = document.querySelectorAll(
            '.klaviyoformtrigger'
          );
          klaviyoTriggers.forEach((trigger) => {
            const klaviyoId = trigger.getAttribute('data-klaviyo');
            trigger.addEventListener('click', () => {
              window._klOnsite = window._klOnsite || [];
              window._klOnsite.push(['openForm', klaviyoId]);
            });
          });
        },
      },
    }).$mount(incentiveCarousel);
  });

// Collection Carousels
document
  .querySelectorAll('.collection-carousel')
  .forEach((collectionCarousel) => {
    const ID = collectionCarousel.parentNode.id;
    window.PSTheme.Vue[`CollectionCarousel${ID}`] = new Vue({
      name: 'CollectionCarousel',
      components: {
        slider: Splide,
        slide: SplideSlide,
      },
      data() {
        return {
          mounted: false,
          sliderOptions: {
            perPage: 3,
            arrows: true,
            pagination: false,
            gap: '1rem',
            drag: 'free',
            breakpoints: {
              [px_breakpoints['--tablet-lg-up']]: {
                arrows: false,
                pagination: false,
              },
              [px_breakpoints['--mobile-up']]: {
                type: 'loop',
                padding: '5rem',
                perPage: 1,
                arrows: true,
                pagination: false,
              },
            },
          },
        };
      },
      mounted() {
        this.mounted = true;
      },
    }).$mount(collectionCarousel);
  });
// Brand Carousels
document.querySelectorAll('.brands-carousel').forEach((collectionBrand) => {
  const ID = collectionBrand.parentNode.id;
  const isautoplay = collectionBrand.classList.contains('autoplay-enabled');
  window.PSTheme.Vue[`CollectionBrand${ID}`] = new Vue({
    name: 'CollectionBrand',
    components: {
      slider: Splide,
      slide: SplideSlide,
    },
    data() {
      return {
        mounted: false,
        sliderOptions: {
          perPage: 3,
          perMove: 1,
          arrows: false,
          pagination: false,
          gap: '1rem',
          type: 'loop',
          autoplay: isautoplay,
          interval: 50,
          speed: 3000,
          drag: 'free',
          breakpoints: {
            [px_breakpoints['--tablet-lg-up']]: {
              arrows: false,
              pagination: false,
            },
            [px_breakpoints['--mobile-up']]: {
              type: 'loop',
              padding: '5rem',
              perPage: 1,
              arrows: false,
              pagination: false,
            },
          },
        },
      };
    },
    mounted() {
      this.mounted = true;
    },
  }).$mount(collectionBrand);
});
// Product Carousels
document.querySelectorAll('.product-carousel').forEach((productCarousel) => {
  const ID = productCarousel.parentNode.id;
  window.PSTheme.Vue[`ProductSlider_${ID}`] = new Vue({
    name: 'ProductSlider',
    components: {
      slider: Splide,
      slide: SplideSlide,
      ProductItem,
    },
    data() {
      return {
        mounted: false,
        sliderOptions: {
          perPage: 5,
          arrows: true,
          pagination: false,
          gap: '1.25rem',
          drag: 'free',
          breakpoints: {
            [px_breakpoints['--tablet-lg-up']]: {
              type: 'loop',
              gap: '1rem',
              perPage: 4,
              arrows: true,
              pagination: false,
            },
            [px_breakpoints['--tablet-up']]: {
              perPage: 3,
              arrows: true,
              pagination: false,
            },
            [px_breakpoints['--mobile-up']]: {
              type: 'loop',
              padding: '7rem',
              perPage: 1,
              arrows: true,
              pagination: false,
            },
          },
        },
      };
    },
    mounted() {
      this.mounted = true;
    },
  }).$mount(productCarousel);
});

// Product Carousels Tab
document
  .querySelectorAll('.product-carousel-tab')
  .forEach((productCarousel) => {
    const ID = productCarousel.parentNode.id;
    window.PSTheme.Vue[`ProductSlider_${ID}`] = new Vue({
      name: 'ProductSlider',
      components: {
        slider: Splide,
        slide: SplideSlide,
        ProductItem,
      },
      data() {
        return {
          mounted: false,
          sliderOptions: {
            perPage: 5,
            arrows: true,
            pagination: false,
            gap: '1.25rem',
            drag: 'free',
            breakpoints: {
              [px_breakpoints['--tablet-lg-up']]: {
                type: 'loop',
                gap: '1rem',
                perPage: 4,
                arrows: false,
                pagination: true,
              },
              [px_breakpoints['--tablet-up']]: {
                perPage: 3,
                arrows: false,
                pagination: true,
              },
              [px_breakpoints['--mobile-up']]: {
                type: 'loop',
                padding: '7rem',
                perPage: 1,
                arrows: false,
                pagination: true,
              },
            },
          },
        };
      },
      mounted() {
        this.mounted = true;
      },
    }).$mount(productCarousel);
  });

// Blog Carousels
document.querySelectorAll('.blog-carousel').forEach((blogCarousel) => {
  const ID = blogCarousel.parentNode.id;
  window.PSTheme.Vue[`BlogSlider_${ID}`] = new Vue({
    name: 'BlogSlider',
    components: {
      slider: Splide,
      slide: SplideSlide,
    },
    data() {
      return {
        mounted: false,
        sliderOptions: {
          perPage: 3,
          arrows: true,
          pagination: false,
          gap: '1.25rem',
          drag: 'free',
          breakpoints: {
            [px_breakpoints['--tablet-lg-up']]: {
              type: 'loop',
              gap: '1rem',
              arrows: false,
              pagination: true,
            },
            [px_breakpoints['--tablet-up']]: {
              perPage: 2,
            },
            [px_breakpoints['--mobile-up']]: {
              padding: '5rem',
              perPage: 1,
            },
          },
        },
      };
    },
    mounted() {
      this.mounted = true;
    },
  }).$mount(blogCarousel);
});

// Product
if (document.body.classList.contains('template-product')) {
  window.PSTheme.Vue.Product = new Vue({
    name: 'Product',

    components: { Inventory },

    data() {
      return {
        isDrawerOpen: false,
      };
    },

    mounted() {
      const $stockBtn = document.querySelector('.btn-check-stock');

      if ($stockBtn) {
        $stockBtn.addEventListener('click', () => {
          emitter.emit('open:drawer', 'stock');
          this.isDrawerOpen = true;
        });
      }

      const $sizeGuideBtn = document.querySelector('.btn-size-guide');

      if ($sizeGuideBtn) {
        $sizeGuideBtn.addEventListener('click', () => {
          emitter.emit('open:drawer', 'sizing');
          this.isDrawerOpen = true;
        });
      }

      emitter.on('close:drawer', () => {
        this.isDrawerOpen = false;
      });
    },

    watch: {
      isDrawerOpen(newVal) {
        if (newVal === false) {
          emitter.emit('close:drawer');
        }
      },
    },
  }).$mount('.product-inventory');
}

if (document.body.classList.contains('template-index')) {
  window.PSTheme.Vue.Stores = new Vue({
    name: 'Stores',
    props: ['blocks'],
    data() {
      return {
        topIndex: 1,
      };
    },
  }).$mount('.stores');
}

if (document.body.classList.contains('template-cart')) {
  window.PSTheme.Vue.Cart = new Vue({
    name: 'Cart',
    components: { Modal },
    data() {
      return {
        mounted: false,
        loading: false,
        showCartNotes: false,
        showResults: false,
        giftCode: '',
        resultMessage: '',
        giftSuccess: false,
        cartTotalPrice: 0,
        giftCardCode: '',
        showModal: false,
        giftWrapNote: '',
        giftCardNumberLoading: false,
        giftCardNumber: '',
        giftCardBalance: 0,
        giftCardErrorMsg: '',
        showGiftCardBalance: false,
        showGiftCardBalanceError: false,
      };
    },
    mounted() {
      this.updateUiOnPageRefresh();
      this.mounted = true;
      this.giftWrapNote = $('[data-gift-note]').data('gift-note');
    },

    computed: {
      hasCode() {
        return !(this.giftCode.length > 0);
      },
      hasGiftCardNumber() {
        return !(this.giftCardNumber.length > 0);
      },
      formatGiftCardBalance() {
        return `$${this.giftCardBalance.toFixed(2)}`;
      },
    },

    methods: {
      checkCarbon(el) {
        $('#cc-add-button').trigger('click');
      },
      donation(variantId, check) {
        // add donation product
        let config = {};

        if (check) {
          config = {
            method: 'post',
            url: '/cart/change.js',
            data: {
              id: `${variantId}`,
              quantity: 0,
            },
          };
        } else {
          config = {
            method: 'post',
            url: '/cart/add.js',
            data: {
              id: variantId,
              quantity: 1,
            },
          };
        }
        axios(config)
          .then(() => window.location.reload())
          .catch((err) => console.error(err));
      },
      applyGiftCard() {
        const vm = this;
        vm.cartTotalPrice = parseInt(vm.$refs.inputCart.value, 10);
        vm.loading = true;
        vm.showResults = false;

        const data = JSON.stringify({
          card: vm.giftCode,
        });

        const config = {
          method: 'post',
          url: 'https://floandfrankie.com/apps/ps/get_balance',
          headers: {
            'Content-Type': 'application/json',
          },
          data,
        };

        axios(config)
          .then((response) => {
            const cardData = response.data.data;
            if (response.data.status === 0) {
              // * SUCCESS
              if (cardData.balance > 0) {
                vm.addGiftCardToFirstItem(cardData);
              } else {
                vm.loading = false;
                vm.giftSuccess = false;
                vm.updateResults(
                  'Oops! This gift card cannot be applied to this order. <a href="/pages/contact-us">Contact us</a> if there’s been a mistake.'
                );
              }
            } else {
              // * SOMETHING IS WRONG
              vm.loading = false;
              vm.giftSuccess = false;
              vm.updateResults(
                'Oops! This gift card cannot be applied to this order. <a href="/pages/contact-us">Contact us</a> if there’s been a mistake.'
              );
            }
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.log(error);
          });
      },
      checkBalance() {
        const giftCardNumberObj = this;
        giftCardNumberObj.showGiftCardBalance = false;
        giftCardNumberObj.showGiftCardBalanceError = false;
        giftCardNumberObj.giftCardNumberLoading = true;
        giftCardNumberObj.giftCardErrorMsg = '';
        const data = JSON.stringify({
          card: giftCardNumberObj.giftCardNumber,
        });

        const config = {
          method: 'post',
          url: 'https://floandfrankie.com/apps/ps/get_balance',
          headers: {
            'Content-Type': 'application/json',
          },
          data,
        };
        axios(config)
          .then((response) => {
            giftCardNumberObj.giftCardNumberLoading = false;
            if (response.data.status === 0) {
              giftCardNumberObj.giftCardBalance = response.data.data.balance;
              giftCardNumberObj.showGiftCardBalance = true;
            } else {
              giftCardNumberObj.giftCardErrorMsg = response.data.message;
              giftCardNumberObj.showGiftCardBalanceError = true;
            }
          })
          .catch((error) => {
            console.log(error);
            giftCardNumberObj.giftCardErrorMsg =
              'This gift card cannot be found.';
            giftCardNumberObj.giftCardNumberLoading = false;
          });
      },
      addGiftCardToFirstItem(currentGiftData) {
        const vm = this;
        let storedGifts = ls('giftCards');

        const firstItemQuantity = parseInt(
          vm.$refs['cart_first-quantity'].innerHTML,
          10
        );

        if (storedGifts) {
          const index = storedGifts.findIndex(
            (object) => object.code === currentGiftData.code
          );
          // Add card to object if not exist
          if (index === -1) {
            storedGifts.push(currentGiftData);
          }
        } else {
          storedGifts = [currentGiftData];
        }

        // Save all gift cards to localStore
        ls.set('giftCards', storedGifts);

        const myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/json');

        const raw = JSON.stringify({
          line: 1,
          quantity: firstItemQuantity,
          properties: {
            _cards: storedGifts,
          },
        });

        const requestOptions = {
          method: 'POST',
          headers: myHeaders,
          body: raw,
          redirect: 'follow',
        };

        fetch('https://floandfrankie.com/cart/change.js', requestOptions)
          .then((response) => response.text())
          .then(() => {
            vm.giftSuccess = true;

            window.location.reload();
          })
          // eslint-disable-next-line no-console
          .catch((error) => console.log('error', error));
      },
      giftWrapping(id) {
        const attributes = { 'gift-wrap-note': this.giftWrapNote };
        // add gift wrapping product

        if (id) {
          const item = {
            id,
            quantity: 1,
          };
          // $this.addClass('disabled');

          $.ajax({
            type: 'POST',
            url: '/cart/add.js',
            data: item,
            dataType: 'json',
          })
            .done(() => {
              jQuery.post(`${window.Shopify.routes.root}cart/update.js`, {
                attributes,
              });
              window.location.reload();
            })
            .fail(() => {
              // On fail
              alert(
                'Error adding gift wrapping to your order, please try again.'
              );
              // $this.removeClass('disabled');
            });
        }
      },
      updateUiOnPageRefresh() {
        const vm = this;
        const giftCards = ls('giftCards');
        if ($('.gift-card-code').length > 0)
          $('.gift-card-code').text(giftCards[0].code);

        vm.cartTotalPrice = parseInt(vm.$refs.inputCart.value);

        // Check if any item in the cart has discount applied
        axios.get('/cart.js').then((response) => {
          const cartItems = response.data.items;
          for (let i = 0; i < cartItems.length; i++) {
            if (cartItems[i].hasOwnProperty('properties')) {
              if (cartItems[i].properties.hasOwnProperty('_cards')) {
                if (giftCards) {
                  vm.giftSuccess = true;
                  // eslint-disable-next-line guard-for-in
                }
              }
            }
          }
        });
      },
    },
  }).$mount('.cart__footer');

  window.PSTheme.Vue.Wishlist = new Vue({
    name: 'Wishlist',
    components: {
      slider: Splide,
      slide: SplideSlide,
      UpsellItem,
    },
    data() {
      return {
        mounted: false,
        products: [],
        sliderOptions: {
          perPage: 3,
          arrows: true,
          pagination: false,
          gap: '1rem',
          drag: 'free',
          breakpoints: {
            [px_breakpoints['--tablet-lg-up']]: {
              arrows: false,
              pagination: true,
            },
            [px_breakpoints['--mobile-up']]: {
              type: 'loop',
              padding: '5rem',
              perPage: 1,
              arrows: false,
              pagination: true,
            },
          },
        },
      };
    },
    beforeMount() {},
    mounted() {
      if (ls.get('iWishlistmain')) {
        this.getWishlist();
      }
      this.mounted = true;
    },
    methods: {
      async getWishlist() {
        const wishlist = ls.get('iWishlistmain');

        const pIDs = [];

        // Getting product id from wishlist
        Object.keys(wishlist).forEach((item) => {
          pIDs.push(`gid://shopify/Product/${item}`);
        });

        const gqlData = {
          query: print(fetchWishlist),
          variables: {
            ids: pIDs,
          },
        };

        const config = {
          method: 'POST',
          url: '/api/2021-10/graphql.json',
          headers: {
            'X-Shopify-Storefront-Access-Token':
              '70d7b84c9c5780031e78dc9924309f0f',
            'Content-Type': 'application/json',
          },
          data: gqlData,
        };
        axios(config)
          .then((res) => {
            this.products = res.data.data.nodes;
          })
          .then(() => {
            this.initSlider();
          })
          .catch((err) => {
            console.log(err);
          });
      },
      initSlider() {
        const $wishlistSlider = $('.cart-wishlist__slider');

        if ($wishlistSlider.length) {
          $wishlistSlider.slick({
            accessibility: false, // Prevents jump to this slider on page load
            dots: false,
            draggable: true,
            waitForAnimate: false,
            rows: 0,
            slidesToShow: 6,
            slidesToScroll: 6,
            infinite: true,
            responsive: [
              {
                breakpoint: 1280,
                settings: {
                  slidesToShow: 3,
                  slidesToScroll: 3,
                },
              },
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 2,
                },
              },
            ],
          });
        }
      },
    },
  }).$mount('.cart__wishlist');
}

// Get the search icon and search input elements
const searchIcon = document.getElementById('searchIcon');
const searchInput = document.getElementById('searchInput');
const searchInputClose = document.getElementById('searchInputClose');
const FSautocomplete = document.getElementsByClassName('fast-ac-container');

// Add a click event listener to the search icon
if (searchIcon) {
  searchIcon.addEventListener('click', (e) => {
    // Toggle the display style between 'block' and 'none'
    e.preventDefault();
    e.stopPropagation();
    if (
      searchInput.style.display === 'none' ||
      searchInput.style.display === ''
    ) {
      searchInput.style.display = 'flex';
      const actualInput = searchInput.querySelector('input'); // Adjust the selector if necessary
      if (actualInput) {
        actualInput.focus();
      }

      if (FSautocomplete.length >= 1) {
        for (let i = 0; i < FSautocomplete.length; i++) {
          FSautocomplete[i].style.setProperty('display', 'block');
        }
      }
    } else {
      searchInput.style.display = 'none';
      if (FSautocomplete.length >= 1) {
        for (let i = 0; i < FSautocomplete.length; i++) {
          FSautocomplete[i].style.setProperty('display', 'none');
        }
      }
    }
  });
}
if (searchInputClose) {
  searchInputClose.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    searchInput.style.display = 'none';
    if (FSautocomplete.length >= 1) {
      for (let i = 0; i < FSautocomplete.length; i++) {
        FSautocomplete[i].style.setProperty('display', 'none');
      }
    }
  });
}
